import { LitElement, html, svg, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@inmar/component-lib';

import styles from './csv-import-validation-summary.css?inline';

import type { ImportResultSummary } from '../../types/template';
import { SVG_ICONS } from '../../constants/inline-icons.js';

// ============================================================================
// Types
// ============================================================================

export type ReviewValidationSummary = ImportResultSummary;

export const EMPTY_VALIDATION_SUMMARY = Object.freeze({
  totalRows: 0,
  validCount: 0,
  invalidCount: 0,
  duplicateCount: 0,
}) satisfies ReviewValidationSummary;

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementTagNameMap {
    'csv-import-validation-summary': CsvImportValidationSummary;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Reusable validation summary card displaying valid/error/duplicate counts.
 *
 * Renders a bordered card with a `<dl>` containing three label–value pairs
 * (valid records, records with errors, duplicate records) and an optional
 * `<slot name="actions">` for consumer-provided buttons.
 *
 * This component is purely presentational — it owns no workflow state or
 * event dispatch logic. Parents compose it with their own actions via slots.
 *
 * Replace the `summary` object rather than mutating it in place so Lit
 * observes the change.
 */
@customElement('csv-import-validation-summary')
export class CsvImportValidationSummary extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  /**
   * Summary counts for the validation result.
   *
   * Replace the object instead of mutating it in place so Lit schedules an
   * update when count values change.
   */
  @property({ type: Object, attribute: false })
  summary: ReviewValidationSummary = EMPTY_VALIDATION_SUMMARY;

  private get _summary(): ReviewValidationSummary {
    return { ...EMPTY_VALIDATION_SUMMARY, ...(this.summary ?? {}) };
  }

  override render() {
    const summary = this._summary;

    return html`
      <dl class="summary-card" aria-label="Validation summary">
        <div class="summary-items">
          <div class="summary-item summary-item--valid">
            <dt>
              <span class="summary-icon" aria-hidden="true">
                <hcds-icon
                  name="check-circle"
                  family="classic"
                  icon-style="light"
                ></hcds-icon>
              </span>
              <strong>${summary.validCount}</strong>
              ${summary.validCount === 1
                ? 'record is valid'
                : 'records are valid'}
            </dt>
          </div>
          <div class="summary-item summary-item--error">
            <dt>
              <span class="summary-icon" aria-hidden="true">
                ${this._renderSvgIcon('exclamation-circle')}
              </span>
              <strong>${summary.invalidCount}</strong>
              ${summary.invalidCount === 1
                ? 'record has errors'
                : 'records have errors'}
            </dt>
          </div>
          <div class="summary-item summary-item--duplicate">
            <dt>
              <span class="summary-icon" aria-hidden="true">
                ${this._renderSvgIcon('clone')}
              </span>
              <strong>${summary.duplicateCount}</strong>
              ${summary.duplicateCount === 1
                ? 'record is a duplicate'
                : 'records are duplicates'}
            </dt>
          </div>
        </div>
        <div class="summary-actions">
          <slot name="actions"></slot>
        </div>
      </dl>
    `;
  }

  private _renderSvgIcon(name: keyof typeof SVG_ICONS) {
    const icon = SVG_ICONS[name];
    return svg`
      <svg
        class="summary-icon-svg"
        viewBox=${icon.viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        <path d=${icon.path} fill="currentColor" />
      </svg>
    `;
  }
}
